import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaMapMarkerAlt,
  FaBirthdayCake,
  FaPassport,
  FaGraduationCap,
  FaHome,
  FaUserFriends,
  FaHeart,
  FaCalendarAlt,
  FaChevronDown,
  FaLock
} from "react-icons/fa";

// Care Leaver Options
const CARE_LEAVER_OPTIONS = [
  "Select Care Leaver Status",
  "Care leaver (16+)",
  "In care in the rest of UK",
  "Information refused",
  "Looked after in Scotland",
  "Not a care leaver",
  "Not known",
  "UCAS defined care leaver",
];

// List of UK country variations
const UK_COUNTRY_VARIANTS = [
  "uk",
  "united kingdom",
  "great britain",
  "britain",
  "england",
  "scotland",
  "wales",
  "northern ireland",
  "gb",
  "u.k.",
  "united kingdom of great britain and northern ireland",
];

const RegisterPage2 = ({
  formData = {},
  initialData = null,
  onSubmit,
  onPrevious,
  onDataChange,
}) => {
  const [countries, setCountries] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVisaRejection, setShowVisaRejection] = useState(false);
  const [showDateOfEntryUK, setShowDateOfEntryUK] = useState(false);
  
  // Add state for user role
  const [userRole, setUserRole] = useState("user");
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize form data state
  const [localFormData, setLocalFormData] = useState({
    // Personal Information
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNo: "",
    nationality: "",
    countryOfBirth: "",
    ethnicOrigin: "",
    dob: "",
    gender: "",
    highestQualification: "",
    lastInstitute: "",
    passportNo: "",
    passportExpiry: "",
    passportPlaceOfIssuance: "",
    careLeaver: "",
    visaRejection: "",
    maritalStatus: "",
    dateOfEntryUK: "",

    // Correspondence Address
    corrPostalCode: "",
    corrAddressLine1: "",
    corrAddressLine2: "",
    corrAddressLine3: "",
    corrCity: "",
    corrCountry: "",
    corrPhoneNo: "",

    // Permanent Address
    sameAsCorrespondence: false,
    permPostalCode: "",
    permAddressLine1: "",
    permAddressLine2: "",
    permAddressLine3: "",
    permCity: "",
    permCountry: "",
    permPhoneNo: "",

    // Next of Kin
    nextOfKinName: "",
    nextOfKinRelation: "",
    nextOfKinPhoneNo: "",
    nextOfKinEmail: "",
  });

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = () => {
      const userData = localStorage.getItem("user");

      if (!userData) {
        setUserRole("user");
        setIsAdmin(false);
        return;
      }

      try {
        const parsedData = JSON.parse(userData);
        const role = (parsedData.role || "user").toLowerCase();

        setUserRole(role);
        setIsAdmin(role === "admin");

      } catch (error) {
        console.error("Error parsing user data:", error);
        setUserRole("user");
        setIsAdmin(false);
      }
    };

    checkUserRole();
  }, []);

  // Update local form data when prop changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setLocalFormData((prev) => ({
        ...prev,
        ...formData,
      }));
    }
  }, [formData]);

  // Check if country is UK (case insensitive and handles common variations)
  const isUKCountry = (countryValue) => {
    if (!countryValue) return false;

    // If it's a number (country ID), find the country name
    if (typeof countryValue === "number" || !isNaN(countryValue)) {
      const countryId = parseInt(countryValue);
      const country = countries.find((c) => c.id === countryId);
      if (country) {
        return UK_COUNTRY_VARIANTS.includes(
          country.display_name.toLowerCase().trim()
        );
      }
      return false;
    }

    // If it's a string (country name)
    return UK_COUNTRY_VARIANTS.includes(countryValue.toLowerCase().trim());
  };

  // Check residency from localStorage to determine if visa rejection field should show
  useEffect(() => {
    const checkResidency = () => {
      try {
        const register1Data = localStorage.getItem("register_1");
        if (register1Data) {
          const parsedData = JSON.parse(register1Data);
          if (parsedData.residency === "31") {
            setShowVisaRejection(true);
            return;
          }
        }
      } catch (error) {
        console.error("Error parsing register_1 data:", error);
      }
      setShowVisaRejection(false);
    };

    checkResidency();
  }, []);

  // Check if country of birth is UK and show/hide date of entry field
  useEffect(() => {
    const isUK = isUKCountry(localFormData.countryOfBirth);
    setShowDateOfEntryUK(!isUK);

    if (isUK) {
      handleDataChange({ dateOfEntryUK: "" });
    }
  }, [localFormData.countryOfBirth, countries]);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/countries`
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} - ${res.statusText}`);
        }
        const data = await res.json();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };

    fetchCountries();
  }, []);

  // Helper function to find country ID by name
  const resolveCountryId = (value) => {
    if (!value || !countries.length) return "";

    // If value is already an ID (numeric or numeric string)
    if (!isNaN(value)) {
      return value.toString();
    }

    // Otherwise match by name
    const match = countries.find(
      (c) => c.display_name.toLowerCase() === value.toLowerCase()
    );

    return match ? match.id.toString() : "";
  };

  // Auto-fill form data from initialData when component mounts
  useEffect(() => {
    if (initialData && countries.length > 0) {
      console.log("Initial data received:", initialData);

      const autoFillData = {
        // Personal info
        title: initialData.personal_info?.title || "",
        firstName: initialData.user?.first_name || "",
        lastName: initialData.user?.last_name || "",
        email: initialData.user?.email || "",
        mobileNo: initialData.personal_info?.mobile_no || "",
        nationality: initialData.personal_info?.nationality || "",

        // Country of Birth - convert name to ID
        countryOfBirth: resolveCountryId(
          initialData.personal_info?.country_of_birth
        ),

        ethnicOrigin: initialData.personal_info?.ethnic_origin || "",
        dob:
          initialData.personal_info?.dob &&
          initialData.personal_info.dob !== "0001-01-01"
            ? initialData.personal_info.dob
            : "",
        gender: initialData.personal_info?.gender || "",
        highestQualification:
          initialData.personal_info?.highest_qualification || "",
        lastInstitute: initialData.personal_info?.last_institute || "",
        passportNo: initialData.personal_info?.passport_no || "",
        passportExpiry:
          initialData.personal_info?.passport_expiry &&
          initialData.personal_info.passport_expiry !== "0001-01-01"
            ? initialData.personal_info.passport_expiry
            : "",
        passportPlaceOfIssuance:
          initialData.personal_info?.passport_issuance_place || "",
        careLeaver: initialData.personal_info?.care_leaver || "",
        visaRejection: initialData.personal_info?.visa_rejection || "",
        maritalStatus: initialData.personal_info?.marital_status || "",
        dateOfEntryUK:
          initialData.personal_info?.date_of_entry &&
          initialData.personal_info.date_of_entry !== "0001-01-01"
            ? initialData.personal_info.date_of_entry
            : "",

        // Correspondence Address
        corrPostalCode: initialData.correspondence_address?.postal_code || "",
        corrAddressLine1:
          initialData.correspondence_address?.address_line_1 || "",
        corrAddressLine2:
          initialData.correspondence_address?.address_line_2 || "",
        corrAddressLine3:
          initialData.correspondence_address?.address_line_3 || "",
        corrCity: initialData.correspondence_address?.city || "",
        corrCountry: initialData.correspondence_address?.country || "",
        corrPhoneNo: initialData.correspondence_address?.phone_no || "",

        // Permanent Address
        permPostalCode: initialData.permanent_address?.postal_code || "",
        permAddressLine1: initialData.permanent_address?.address_line_1 || "",
        permAddressLine2: initialData.permanent_address?.address_line_2 || "",
        permAddressLine3: initialData.permanent_address?.address_line_3 || "",
        permCity: initialData.permanent_address?.city || "",
        permCountry: initialData.permanent_address?.country || "",
        permPhoneNo: initialData.permanent_address?.phone_no || "",

        // Next of Kin
        nextOfKinName: initialData.next_of_kin?.name || "",
        nextOfKinRelation: initialData.next_of_kin?.relation || "",
        nextOfKinPhoneNo: initialData.next_of_kin?.phone_no || "",
        nextOfKinEmail: initialData.next_of_kin?.email || "",
      };

      console.log("Auto-fill data prepared:", autoFillData);

      // Update local state
      setLocalFormData((prev) => ({
        ...prev,
        ...autoFillData,
      }));

      // Notify parent component
      if (onDataChange) {
        onDataChange(autoFillData);
      }
    }
  }, [initialData, countries]);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem("register_2", JSON.stringify(localFormData));
  }, [localFormData]);

  // Copy correspondence address to permanent address when checkbox is checked
  useEffect(() => {
    if (localFormData.sameAsCorrespondence && isAdmin) {
      const updates = {
        permPostalCode: localFormData.corrPostalCode,
        permAddressLine1: localFormData.corrAddressLine1,
        permAddressLine2: localFormData.corrAddressLine2,
        permAddressLine3: localFormData.corrAddressLine3,
        permCity: localFormData.corrCity,
        permCountry: localFormData.corrCountry,
        permPhoneNo: localFormData.corrPhoneNo,
      };

      setLocalFormData((prev) => ({
        ...prev,
        ...updates,
      }));

      if (onDataChange) {
        onDataChange(updates);
      }
    }
  }, [
    localFormData.sameAsCorrespondence,
    localFormData.corrPostalCode,
    localFormData.corrAddressLine1,
    localFormData.corrAddressLine2,
    localFormData.corrAddressLine3,
    localFormData.corrCity,
    localFormData.corrCountry,
    localFormData.corrPhoneNo,
    isAdmin,
  ]);

  // Modified handleDataChange to prevent changes for non-admin users
  const handleDataChange = (updates) => {
    // If not admin, prevent changes
    if (!isAdmin) {
      return;
    }

    setLocalFormData((prev) => ({
      ...prev,
      ...updates,
    }));

    if (onDataChange) {
      onDataChange(updates);
    }
  };

  // Modified handleChange to prevent changes for non-admin users
  const handleChange = (e) => {
    // If not admin, prevent changes
    if (!isAdmin) {
      return;
    }

    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    handleDataChange({ [name]: newValue });

    if (name === "sameAsCorrespondence" && !checked) {
      const clearUpdates = {
        permPostalCode: "",
        permAddressLine1: "",
        permAddressLine2: "",
        permAddressLine3: "",
        permCity: "",
        permCountry: "",
        permPhoneNo: "",
      };

      setLocalFormData((prev) => ({
        ...prev,
        ...clearUpdates,
      }));

      if (onDataChange) {
        onDataChange(clearUpdates);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Always allow submission regardless of admin status
    setIsSubmitting(true);

    if (onSubmit) {
      onSubmit(localFormData);
    }

    setTimeout(() => {
      setIsSubmitting(false);
    }, 1000);
  };

  // Get personal info fields with conditional visa rejection field
  const getPersonalInfoFields = () => {
    const baseFields = [
      {
        label: "Title *",
        name: "title",
        type: "select",
        options: [
          { value: "", label: "Select Title" },
          { value: "mr", label: "Mr" },
          { value: "ms", label: "Ms" },
          { value: "mrs", label: "Mrs" },
          { value: "dr", label: "Dr" },
          { value: "prof", label: "Prof" },
        ],
        icon: <FaUser className="text-gray-400 text-sm" />,
      },
      {
        label: "First Name *",
        name: "firstName",
        placeholder: "Enter first name",
        icon: <FaUser className="text-gray-400 text-sm" />,
      },
      {
        label: "Last Name *",
        name: "lastName",
        placeholder: "Enter last name",
        icon: <FaUser className="text-gray-400 text-sm" />,
      },
      {
        label: "E-mail Address *",
        name: "email",
        type: "email",
        placeholder: "Enter email",
        icon: <FaEnvelope className="text-gray-400 text-sm" />,
      },
      {
        label: "Mobile No *",
        name: "mobileNo",
        placeholder: "Enter mobile number",
        icon: <FaPhone className="text-gray-400 text-sm" />,
      },
      {
        label: "Nationality *",
        name: "nationality",
        placeholder: "Enter nationality",
        icon: <FaGlobe className="text-gray-400 text-sm" />,
      },
      {
        label: "Country of Birth *",
        name: "countryOfBirth",
        type: "select",
        options: [
          { value: "", label: "Select Country" },
          ...countries.map((c) => ({
            value: c.id, // Use country ID
            label: c.display_name,
          })),
        ],
        icon: <FaMapMarkerAlt className="text-gray-400 text-sm" />,
      },
      {
        label: "Ethnic Origin *",
        name: "ethnicOrigin",
        placeholder: "Enter ethnic origin",
        icon: <FaGlobe className="text-gray-400 text-sm" />,
      },
      {
        label: "Date of Birth *",
        name: "dob",
        type: "date",
        icon: <FaBirthdayCake className="text-gray-400 text-sm" />,
      },
      {
        label: "Gender *",
        name: "gender",
        type: "select",
        options: [
          { value: "", label: "Select Gender" },
          { value: "male", label: "Male" },
          { value: "female", label: "Female" },
          { value: "other", label: "Other" },
        ],
        icon: <FaUser className="text-gray-400 text-sm" />,
      },
      {
        label: "Highest Qualification",
        name: "highestQualification",
        placeholder: "Bachelor, Master, etc.",
        icon: <FaGraduationCap className="text-gray-400 text-sm" />,
      },
      {
        label: "Last Institute *",
        name: "lastInstitute",
        placeholder: "Enter last institute",
        icon: <FaGraduationCap className="text-gray-400 text-sm" />,
      },
      {
        label: "Passport No *",
        name: "passportNo",
        placeholder: "Enter passport number",
        icon: <FaPassport className="text-gray-400 text-sm" />,
      },
      {
        label: "Passport Expiry *",
        name: "passportExpiry",
        type: "date",
        icon: <FaPassport className="text-gray-400 text-sm" />,
      },
      {
        label: "Passport Place of Issuance *",
        name: "passportPlaceOfIssuance",
        placeholder: "Enter place of issuance",
        icon: <FaPassport className="text-gray-400 text-sm" />,
      },
    ];

    if (showVisaRejection) {
      baseFields.push({
        label: "Has Your Visa been Rejected? *",
        name: "visaRejection",
        type: "select",
        options: [
          { value: "", label: "Select Option" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        icon: <FaPassport className="text-gray-400 text-sm" />,
      });
    }

    if (showDateOfEntryUK) {
      baseFields.push({
        label: "Date of Entry to UK *",
        name: "dateOfEntryUK",
        type: "date",
        icon: <FaCalendarAlt className="text-gray-400 text-sm" />,
      });
    }

    baseFields.push({
      label: "Marital Status *",
      name: "maritalStatus",
      type: "select",
      options: [
        { value: "", label: "Select Status" },
        { value: "single", label: "Single" },
        { value: "married", label: "Married" },
        { value: "divorced", label: "Divorced" },
        { value: "widowed", label: "Widowed" },
        { value: "separated", label: "Separated" },
      ],
      icon: <FaHeart className="text-gray-400 text-sm" />,
    });

    return baseFields;
  };

  // Admin Lock Indicator Component
  const AdminLockIndicator = ({ isAdmin }) => {
    if (isAdmin) return null;
    
    return (
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center text-yellow-700">
          <FaLock className="mr-2" />
          <span className="text-sm font-medium">
            View-only mode: Only administrators can edit these fields
          </span>
        </div>
      </div>
    );
  };

  // Get field className based on admin status
  const getFieldClassName = (isDisabledByCondition = false) => {
    if (!isAdmin) {
      return "bg-gray-100 text-gray-500 cursor-not-allowed";
    }
    if (isDisabledByCondition) {
      return "bg-gray-100 text-gray-500 cursor-not-allowed";
    }
    return "";
  };

  return (
    <div className="w-full bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-3 sm:p-4 md:p-6">
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              Personal & Address Information
              {!isAdmin && (
                <span className="ml-3 text-sm bg-yellow-500 text-white px-2 py-1 rounded">
                  View Only
                </span>
              )}
            </h1>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[70vh] p-3 sm:p-4 md:p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Add admin lock indicator */}
            <AdminLockIndicator isAdmin={isAdmin} />
            
            {/* Personal Information Section */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <FaUser className="text-blue-600 text-lg" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800">
                  Personal Information
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {getPersonalInfoFields().map((field, index) => (
                  <div key={index} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <div className="relative">
                      {field.icon && (
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                          {field.icon}
                        </div>
                      )}
                      {field.type === "select" ? (
                        <select
                          name={field.name}
                          value={localFormData[field.name] || ""}
                          onChange={handleChange}
                          disabled={!isAdmin}
                          className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                            field.icon ? "pl-10" : ""
                          } ${!isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                        >
                          {field.options.map((option, i) => (
                            <option key={i} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type || "text"}
                          name={field.name}
                          placeholder={field.placeholder}
                          value={localFormData[field.name] || ""}
                          onChange={handleChange}
                          disabled={!isAdmin}
                          className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                            field.icon ? "pl-10" : ""
                          } ${!isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* Care Leaver - Updated to Dropdown */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Care Leaver Status *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <FaUserFriends className="text-gray-400 text-sm" />
                    </div>
                    <select
                      name="careLeaver"
                      value={localFormData.careLeaver || ""}
                      onChange={handleChange}
                      disabled={!isAdmin}
                      className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none text-sm ${
                        !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'
                      }`}
                    >
                      {CARE_LEAVER_OPTIONS.map((option, index) => (
                        <option
                          key={index}
                          value={
                            option === "Select Care Leaver Status" ? "" : option
                          }
                        >
                          {option}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <FaChevronDown className="text-gray-400 text-sm" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Please select your care leaver status as defined by UCAS
                  </p>
                </div>
              </div>
            </div>

            {/* Correspondence Address Section */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-purple-100 p-2 rounded-lg mr-3">
                  <FaMapMarkerAlt className="text-purple-600 text-lg" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Correspondence Address
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {[
                  {
                    label: "Postal Code *",
                    name: "corrPostalCode",
                    placeholder: "Enter postal code",
                  },
                  {
                    label: "Address Line 1 *",
                    name: "corrAddressLine1",
                    placeholder: "Enter address line 1",
                  },
                  {
                    label: "Address Line 2",
                    name: "corrAddressLine2",
                    placeholder: "Enter address line 2",
                  },
                  {
                    label: "Address Line 3",
                    name: "corrAddressLine3",
                    placeholder: "Enter address line 3",
                  },
                  {
                    label: "City *",
                    name: "corrCity",
                    placeholder: "Enter city",
                  },
                  {
                    label: "Phone No *",
                    name: "corrPhoneNo",
                    placeholder: "Enter phone number",
                  },
                ].map((field, index) => (
                  <div key={index}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <input
                      type="text"
                      name={field.name}
                      placeholder={field.placeholder}
                      value={localFormData[field.name] || ""}
                      onChange={handleChange}
                      disabled={!isAdmin}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                        !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <select
                    name="corrCountry"
                    value={localFormData.corrCountry || ""}
                    onChange={handleChange}
                    disabled={!isAdmin}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                      !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 flex items-center">
                  <label className={`flex items-center ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="sameAsCorrespondence"
                        checked={!!localFormData.sameAsCorrespondence}
                        onChange={handleChange}
                        disabled={!isAdmin}
                        className="sr-only"
                      />
                      <div
                        className={`w-10 h-5 rounded-full transition-colors ${
                          localFormData.sameAsCorrespondence
                            ? "bg-indigo-600"
                            : "bg-gray-300"
                        } ${!isAdmin ? 'opacity-50' : ''}`}
                      >
                        <div
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                            localFormData.sameAsCorrespondence
                              ? "transform translate-x-5"
                              : ""
                          } ${!isAdmin ? 'opacity-70' : ''}`}
                        ></div>
                      </div>
                    </div>
                    <span className={`ml-3 font-medium text-sm ${!isAdmin ? 'text-gray-500' : 'text-gray-700'}`}>
                      Same as Correspondence Address
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Permanent Address Section */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FaHome className="text-blue-600 text-lg" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  Permanent Address
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {[
                  { label: "Postal Code *", name: "permPostalCode" },
                  { label: "Address Line 1 *", name: "permAddressLine1" },
                  { label: "Address Line 2", name: "permAddressLine2" },
                  { label: "Address Line 3", name: "permAddressLine3" },
                  { label: "City *", name: "permCity" },
                  { label: "Phone No *", name: "permPhoneNo" },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <input
                      type="text"
                      name={field.name}
                      value={localFormData[field.name] || ""}
                      onChange={handleChange}
                      disabled={!isAdmin || localFormData.sameAsCorrespondence}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                        !isAdmin || localFormData.sameAsCorrespondence
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <select
                    name="permCountry"
                    value={localFormData.permCountry || ""}
                    onChange={handleChange}
                    required={!localFormData.sameAsCorrespondence}
                    disabled={!isAdmin || localFormData.sameAsCorrespondence}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                      !isAdmin || localFormData.sameAsCorrespondence
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Next of Kin Section */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FaUserFriends className="text-blue-600 text-lg" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Next of Kin</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {[
                  {
                    label: "Name *",
                    name: "nextOfKinName",
                    icon: <FaUser className="text-sm" />,
                  },
                  {
                    label: "Relation *",
                    name: "nextOfKinRelation",
                    icon: <FaUserFriends className="text-sm" />,
                  },
                  {
                    label: "Phone No *",
                    name: "nextOfKinPhoneNo",
                    icon: <FaPhone className="text-sm" />,
                  },
                  {
                    label: "Email *",
                    name: "nextOfKinEmail",
                    type: "email",
                    icon: <FaEnvelope className="text-sm" />,
                  },
                ].map((field) => (
                  <div key={field.name} className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <div className="relative">
                      {field.icon && (
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          {field.icon}
                        </div>
                      )}
                      <input
                        type={field.type || "text"}
                        name={field.name}
                        value={localFormData[field.name] || ""}
                        onChange={handleChange}
                        disabled={!isAdmin}
                        className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                          !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 sm:pt-6">
              <button
                type="button"
                onClick={onPrevious}
                disabled={isSubmitting}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-400 rounded-lg font-medium text-gray-600 hover:bg-gray-50 flex-1 transition-colors duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white"
              >
                Previous
              </button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className={`px-3 sm:px-4 py-2 sm:py-2.5 flex-1 rounded-lg font-medium text-white transition-all duration-300 shadow-md hover:shadow-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed opacity-70"
                    : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:opacity-95"
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center space-x-2">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Processing...</span>
                  </span>
                ) : (
                  "Next Step"
                )}
              </motion.button>
            </div>
          </form>
        </div>
    </div>
  );
};

export default RegisterPage2;